import React from 'react';
import {
  listObjectsInFolder,
  readObjectContent,
  uploadToStorage,
} from '../../utils/storageUtils';
import { writeToFirestore, fetchFirestoreCollections, writeToFirestoreSdk } from '../../utils/firestoreUtils';
import { saveToFileSystem, unflattenData } from '../../utils/dataUtils';
import { getTestFirebaseForAdmin, getCurrentUserEnv } from '../../services/firebase';
import { uploadToStorageSdk } from '../../utils/storageUtils';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { imperativeAlert } from '../../contexts/ToastContext';

/* -----------------------------------------------------------------------
   Small reusable component for the “Overwrite?” checkbox.
   It can safely use React hooks because it is rendered as a component.
   ----------------------------------------------------------------------- */
function OverwriteCheckbox({ values, setValue }) {
  const { location, folder, itemName } = values;
  const [exists, setExists] = React.useState(false);

  React.useEffect(() => {
    const check = async () => {
      try {
        if (!location) return setExists(false);

        if (location === 'bucket') {
          if (!folder) return setExists(false);
          const { items } = await listObjectsInFolder('', folder);
          const names = items.map(i => i.replace(/\.[^.]+$/, ''));
          setExists(names.includes(itemName || ''));
        } else {
          // Firestore – try to read the document
          if (!folder) return setExists(false);
          const resp = await fetch(
            `https://us-central1-pcakedatastore.cloudfunctions.net/readDocument?collection=${folder}&docId=${itemName || ''}`
          );
          setExists(resp.ok);
        }
      } catch (error) {
        console.error('Error checking existence:', error);
        setExists(false);
      }
    };
    check();
  }, [location, folder, itemName]);

  if (!exists) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <label>
        <input
          type="checkbox"
          checked={values.overwrite ?? false}
          onChange={e => setValue('overwrite', e.target.checked)}
        />{' '}
        Overwrite existing {location === 'bucket' ? 'object' : 'document'}
      </label>
    </div>
  );
}

/* -----------------------------------------------------------------------
   Folder Selector Component for bucket folder selection
   ----------------------------------------------------------------------- */
  function FolderSelector({ values, setValue }) {
    const { destBucket } = values;
    const [folders, setFolders] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [customFolder, setCustomFolder] = React.useState('');

    React.useEffect(() => {
      const fetchFolders = async () => {
        if (!destBucket) return;
        
        setLoading(true);
        try {
          // List objects to get folder prefixes
          const { prefixes } = await listObjectsInFolder('', destBucket);
          
          // Extract folder names from prefixes
          const folderList = prefixes.map(prefix => 
            prefix.name.replace(/\/$/, '') // Remove trailing slash
          );
          
          setFolders(folderList);
        } catch (error) {
          console.error('Error fetching folders:', error);
          setFolders([]);
        } finally {
          setLoading(false);
        }
      };

      fetchFolders();
    }, [destBucket]);

    // Handle folder selection from dropdown
    const handleFolderSelect = (folderPath) => {
      setValue('destFolder', folderPath);
      setCustomFolder('');
    };

    // Handle custom folder input
    const handleCustomFolderChange = (e) => {
      const value = e.target.value;
      setCustomFolder(value);
      setValue('destFolder', value);
    };

    return (
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-secondary, #444)' }}>
          Destination Folder
        </label>
        
        {/* Folder Dropdown */}
        <select
          value={values.destFolder || ''}
          onChange={(e) => handleFolderSelect(e.target.value)}
          disabled={loading || !destBucket}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid var(--border-color, #ddd)',
            borderRadius: '4px',
            marginBottom: '8px',
            backgroundColor: loading ? 'var(--bg-section, #f5f5f5)' : 'var(--input-bg, white)',
            color: 'var(--text-primary, #333)'
          }}
        >
          <option value="">Root Folder</option>
          {folders.map((folder, index) => (
            <option key={index} value={folder}>
              {folder}
            </option>
          ))}
        </select>

        {/* Custom Folder Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: 'var(--text-muted, #666)', fontSize: '14px' }}>Or enter custom path:</span>
          <input
            type="text"
            placeholder="e.g., new-folder/sub-folder"
            value={customFolder}
            onChange={handleCustomFolderChange}
            style={{
              flex: 1,
              padding: '6px 8px',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'var(--input-bg, white)',
              color: 'var(--text-primary, #333)'
            }}
          />
        </div>

        {loading && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted, #666)', marginTop: '4px' }}>
            Loading folders...
          </div>
        )}

        {values.destFolder && (
          <div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
            Selected path: <strong>{values.destFolder}</strong>
          </div>
        )}
      </div>
    );
  }

/* -----------------------------------------------------------------------
   Factory – returns a ModalConfig for the three‑step "save" workflow.
   ----------------------------------------------------------------------- */
export function createSaveModalConfig(mode, ctx) {
  const {
    // ----- data that HouseKeeping already knows -------------------------
    fileDataToSave,
    fileFormat,
    isMediaFile,
    fileType,
    selectedFile,
    selectedStoreFile,
    activeConnection,
    selectedBucket,
    currentPath,
    fetchAvailableBuckets,
    fetchStoreFiles,
    setIsBucketModalOpen,
    setIsFileModalOpen,
    setIsFirestoreSaveModalOpen,
    setIsSaveFileModalOpen,
    availableBuckets, 
  } = ctx;

  // Lock destination to Test when the admin is running in the Test environment
  const isTestEnv = getCurrentUserEnv() === 'Test';

  /* -----------------------------------------------------------------
     1️⃣  COMMON helpers used by the three modes
     ----------------------------------------------------------------- */
  const getBaseName = () => {
    const raw = selectedFile?.name ?? selectedStoreFile?.split('/').pop() ?? 'export';
    return raw.replace(/\.[^.]+$/, '');
  };

  const onClose = () => {
    setIsFileModalOpen(false);
    setIsFirestoreSaveModalOpen(false);
    setIsSaveFileModalOpen(false);
    setIsBucketModalOpen(false);
  };

  // Helper function to get MIME type from file name
  const getMimeTypeFromFileName = (fileName) => {
    if (!fileName) return 'application/octet-stream';
  
    const ext = fileName.split('.').pop().toLowerCase();
    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
    
      // Videos
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',
      'mkv': 'video/x-matroska',
      'm4v': 'video/mp4',
      '3gp': 'video/3gpp',
    
      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      
      // Data files
      'csv': 'text/csv',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'json': 'application/json'
    };
  
    return mimeTypes[ext] || 'application/octet-stream';
  };
  /* -----------------------------------------------------------------
     2️⃣  BUILD THE CONFIG OBJECT PER MODE
     ----------------------------------------------------------------- */

  /* -------------------------------------------------------------
     MODE: FILE → LOCAL DOWNLOAD
     ------------------------------------------------------------- */
  if (mode === 'file') {
    return {
      title: 'Save File to Your Computer',
      fields: [
        {
          type: 'text',
          name: 'fileName',
          label: 'File name (without extension)',
          placeholder: getBaseName(),
          required: true,
        },
        {
          type: 'select',
          name: 'format',
          label: 'File format',
          required: true,
          options: [
            { value: 'csv', label: 'CSV' },
            { value: 'excel', label: 'Excel (.xlsx)' },
          ],
          disabled: true, // Make it read-only
          defaultValue: fileFormat, // Pre-select based on current format
        },
      ],
      async onSubmit(values, onClose) {
        try {
          const { fileName, format } = values;
          
          if (!fileDataToSave || fileDataToSave.length === 0) {
            throw new Error('No data available to save');
          }

          const headers = fileDataToSave.length > 0 ? Object.keys(fileDataToSave[0]) : [];

          const finalName = `${fileName}.${format === 'excel' ? 'xlsx' : 'csv'}`;

          saveToFileSystem(fileDataToSave, finalName, format, {
            selectedColumns: headers,
            activeConnection,
          });

          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
        } catch (error) {
          console.error('File save error:', error);
          throw new Error(`Failed to save file: ${error.message}`);
        }
      },

      onClose: () => {
        if (ctx.setFileDataToSave) {
          ctx.setFileDataToSave(null);
        }
      },

      secondaryButton: { 
        label: 'Cancel',
        onClick: () => {
          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
        }
      },
    };
  }


  /* -------------------------------------------------------------
 MODE: COLLECTION → FIRESTORE
 ------------------------------------------------------------- */
  if (mode === 'collection') {
    return {
      title: 'Save to Firestore',
      fields: [
        /* 1️⃣ Destination Firestore Project */
        {
          type: 'select',
          name: 'firestoreProject',
          label: 'Destination Project',
          required: true,
          options: [
            { value: 'Prod', label: 'Prod (pcakedatastore)' },
            { value: 'Test', label: 'Test (pcake-test)' },
          ],
          defaultValue: isTestEnv ? 'Test' : 'Prod',
          disabled: isTestEnv,
        },

        /* 2️⃣ Select existing collection or create new */
        {
          type: 'custom',
          name: 'collection',
          label: 'Select Collection',
          component: ({ values, setValue }) => {
            const isTest = values.firestoreProject === 'Test';
            const [prodCollections, setProdCollections] = React.useState([]);

            // Auto-fetch Prod collections on mount; clear selection when switching to Test
            React.useEffect(() => {
              if (isTest) {
                setValue('collection', '');
              } else {
                fetchFirestoreCollections()
                  .then(cols => setProdCollections(cols || []))
                  .catch(e => console.error('Failed to fetch collections:', e));
              }
            }, [isTest]); // eslint-disable-line react-hooks/exhaustive-deps

            if (isTest) {
              return (
                <div style={{ padding: '8px 10px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '4px', fontSize: '13px', color: '#795548' }}>
                  Collection listing is not available for the Test project — enter the collection name in the field below.
                </div>
              );
            }

            return (
              <select
                value={values.collection || ''}
                onChange={e => setValue('collection', e.target.value)}
                className="dfm-select"
              >
                <option value="">— Select or type new name below —</option>
                {prodCollections.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            );
          },
        },

        /* 3️⃣ New collection name (only shown if "Create New Collection" is selected AND disabled when existing collection is selected) */
        {
          type: 'custom',
          name: 'newCollectionName',
          label: 'New Collection Name',
          component: ({ values, setValue }) => (
            <input
              type="text"
              placeholder="Enter new collection name"
              value={values.newCollectionName || ''}
              onChange={(e) => setValue('newCollectionName', e.target.value)}
              disabled={!!values.collection}
              className="dfm-input"
            />
          ),
          condition: (values) => !values.collection || values.collection === '',
        },

        /* 4️⃣ Document ID/Name - NOW OPTIONAL */
        {
          type: 'text',
          name: 'documentName',
          label: 'Document ID/Name',
          placeholder: getBaseName(),
          required: false, // Changed from true to false
          hint: 'Optional - If provided, data will be saved as a single document',
        },

        /* 5️⃣ Overwrite checkbox – rendered by a real component */
        {
          type: 'custom',
          name: 'overwrite',
          label: '',
          component: OverwriteCheckbox,
        },
      ],

      async onSubmit(values, onClose) {
        const { collection, newCollectionName, documentName, firestoreProject = 'Prod' } = values;
        
        // Determine the target collection
        const targetCollection = collection || newCollectionName;
        if (!targetCollection) {
          imperativeAlert('Please select an existing collection or enter a new collection name.');
          return;
        }

        const doWrite = async (coll, data) => {
          if (firestoreProject === 'Test') {
            const { db: testFirestoreDb } = getTestFirebaseForAdmin();
            await writeToFirestoreSdk(coll, data, testFirestoreDb);
          } else {
            await writeToFirestore(coll, data, false);
          }
        };

        try {
          if (!fileDataToSave || fileDataToSave.length === 0) {
            throw new Error('No data available to save');
          }

          const unflattenedData = fileDataToSave.map(row => unflattenData(row));

          if (documentName) {
            // If document name is provided, save as single document
            let firestoreData;
            
            if (unflattenedData.length > 1) {
              // For multiple records, create a document with a subcollection
              firestoreData = {
                _createdAt: new Date().toISOString(),
                _documentName: documentName,
                records: unflattenedData
              };
            } else if (unflattenedData.length === 1) {
              // For single record, use it as the document data directly
              firestoreData = unflattenedData[0];
            }

            // Save to Firestore as single document - writeToFirestore expects array of objects with id
            await doWrite(targetCollection, [{ id: documentName, ...firestoreData }]);
            
            if (collection) {
              imperativeAlert(`Data saved to existing Firestore collection "${targetCollection}" as document "${documentName}"`);
            } else {
              imperativeAlert(`Data saved to new Firestore collection "${targetCollection}" as document "${documentName}"`);
            }
          } else {
            // If no document name provided, save each record as separate document
            // writeToFirestore expects each object to have an 'id' property
            const documents = unflattenedData.map((record, index) => {
              // Check if record already has an id field
              const hasId = record && record.id !== undefined;
              
              return {
                id: hasId ? record.id : `doc_${Date.now()}_${index}`, // Use existing id or auto-generate
                ...record,
                _createdAt: new Date().toISOString(),
                ...(hasId ? {} : { _autoGenerated: true }) // Only mark as auto-generated if we created the id
              };
            });

            await doWrite(targetCollection, documents);
            
            if (collection) {
              imperativeAlert(`Data saved to existing Firestore collection "${targetCollection}" as ${documents.length} separate documents`);
            } else {
              imperativeAlert(`Data saved to new Firestore collection "${targetCollection}" as ${documents.length} separate documents`);
            }
          }
          
          // Clear data from memory after successful save
          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
          
        } catch (error) {
          console.error('Error saving to Firestore:', error);
          throw new Error(`Failed to save to Firestore: ${error.message}`);
        }
      },

      secondaryButton: { 
        label: 'Cancel', 
        onClick: () => {
          // Clear data from memory on cancel
          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
        }
      },
    };
  }

  /* -------------------------------------------------------------
  MODE: BUCKET → BUCKET (copy / save)
  ------------------------------------------------------------- */
  if (mode === 'bucket') {
    const getBucketTitle = () => {
      const mediaType = fileType || 'file';
      
      if (isMediaFile) {
        const capitalizedType = mediaType.charAt(0).toUpperCase() + mediaType.slice(1);
        return `Save ${capitalizedType} to Bucket`;
      } else {
        return fileFormat === 'excel' ? 'Save Excel to Bucket' : 'Save CSV to Bucket';
      }
    };

    return {
      title: getBucketTitle(),
      fields: [
        /* 1️⃣ Source information - only show if we have a source file */
        ...((selectedFile || selectedStoreFile) ? [
          {
            type: 'custom',
            name: 'sourceInfo',
            label: 'Source',
            component: () => (
              <div style={{ padding: '6px 0', color: 'var(--text-secondary, #555)' }}>
                {selectedFile ? `Local file: ${selectedFile.name}` : 
                selectedStoreFile ? `Storage file: ${selectedStoreFile}` : 
                'New data'}
              </div>
            ),
          },
        ] : []),

        /* 2️⃣ Destination Project */
        {
          type: 'select',
          name: 'destinationProject',
          label: 'Destination Project',
          required: true,
          options: [
            { value: 'Prod', label: 'Prod' },
            { value: 'Test', label: 'Test' },
          ],
          defaultValue: isTestEnv ? 'Test' : 'Prod',
          disabled: isTestEnv,
        },

        /* 3️⃣ Destination bucket – reactive to Destination Project */
        {
          type: 'custom',
          name: 'destBucket',
          label: 'Destination Bucket',
          required: true,
          defaultValue: selectedBucket,
          component: ({ values, setValue }) => {
            const isTest = values.destinationProject === 'Test';
            const testBucket = import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || '';

            // Auto-select the right bucket whenever the project changes
            React.useEffect(() => {
              if (isTest) {
                setValue('destBucket', testBucket);
              } else {
                setValue('destBucket', selectedBucket || (availableBuckets && availableBuckets[0]) || '');
              }
            }, [isTest]); // eslint-disable-line react-hooks/exhaustive-deps

            const opts = isTest
              ? (testBucket ? [{ value: testBucket, label: `${testBucket} (Test)` }] : [])
              : (availableBuckets || []).map(b => ({ value: b, label: b }));

            return (
              <select
                value={values.destBucket || ''}
                onChange={e => setValue('destBucket', e.target.value)}
                className="dfm-select"
              >
                <option value="">— Select —</option>
                {opts.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            );
          },
        },

        /* 3️⃣ Destination folder - UPDATED to use custom component */
        {
          type: 'custom',
          name: 'destFolder',
          label: '', // Label is handled in the custom component
          component: FolderSelector,
        },

        /* 4️⃣ Target file name */
        {
          type: 'text',
          name: 'targetName',
          label: 'Target file name',
          placeholder: getBaseName(),
          required: true,
          defaultValue: getBaseName(), // Pre-fill with base name
        },

        /* 5️⃣ File format for data files (not media) */
        ...(!isMediaFile ? [
          {
            type: 'select',
            name: 'saveFormat',
            label: 'File Format',
            required: true,
            options: [
              { value: 'csv', label: 'CSV' },
              { value: 'excel', label: 'Excel' },
            ],
            defaultValue: fileFormat === 'excel' ? 'excel' : 'csv',
          }
        ] : []),
      ],

      async onSubmit(values, onClose) {
        const {
          destBucket,
          destFolder,
          targetName,
          destinationProject = 'Prod',
          saveFormat = isMediaFile ? 'binary' : (fileFormat === 'excel' ? 'excel' : 'csv')
        } = values;

        const doUpload = async (filePath, file, cType, bucket) => {
          if (destinationProject === 'Test') {
            const { app: testStorageApp } = getTestFirebaseForAdmin();
            await uploadToStorageSdk(filePath, file, cType, bucket, testStorageApp);
          } else {
            await uploadToStorage(filePath, file, cType, bucket);
          }
        };

        try {
          // Validate inputs
          if (!destBucket) {
            throw new Error('Please select a destination bucket');
          }
          if (!targetName) {
            throw new Error('Please enter a target file name');
          }

          let finalFileName = targetName;

          // Handle file extension
          if (isMediaFile) {
            // For media files, if the targetName doesn't have an extension, use the original one.
            if (!finalFileName.includes('.')) {
              const originalFile = selectedFile || selectedStoreFile;
              if (originalFile) {
                const originalName = selectedFile?.name || selectedStoreFile;
                const ext = originalName.split('.').pop() || 'bin';
                finalFileName = `${finalFileName}.${ext}`;
              } else {
                // Default extensions for media files
                const defaultExt = fileType === 'image' ? 'png' : 
                                  fileType === 'video' ? 'mp4' : 
                                  fileType === 'document' ? 'pdf' : 'bin';
                finalFileName = `${finalFileName}.${defaultExt}`;
              }
            }
          } else {
            // For data files, we force the extension to match the format.
            const ext = saveFormat === 'excel' ? 'xlsx' : 'csv';
            // Remove any existing extension and then add the correct one.
            finalFileName = finalFileName.replace(/\.[^/.]+$/, ""); // Remove existing extension
            finalFileName = `${finalFileName}.${ext}`;
          }

          // Include folder path
          let fullFilePath = finalFileName;
          if (destFolder) {
            fullFilePath = `${destFolder}/${finalFileName}`;
          }

          // Sanitize the file path: replace any character that is not alphanumeric, dot, hyphen, underscore, or slash with underscore
          fullFilePath = fullFilePath.replace(/[^a-zA-Z0-9./_-]/g, '_');

          if (isMediaFile) {
            // Handle media file upload
            let fileToUpload;
            let detectedContentType = '';
            
            if (selectedFile) {
              // Local file upload
              fileToUpload = selectedFile;
              detectedContentType = selectedFile.type || getMimeTypeFromFileName(selectedFile.name);
            } else if (selectedStoreFile) {
              // Copy from storage to storage
              
              try {
                // Read the source file
                const sourceResult = await readObjectContent(selectedStoreFile, selectedBucket);
                
                if (!sourceResult.downloadUrl) {
                  throw new Error('No download URL available for source file');
                }
                
                // Download the file
                const response = await fetch(sourceResult.downloadUrl);
                if (!response.ok) {
                  throw new Error(`Failed to download source file: ${response.status}`);
                }
                
                const blob = await response.blob();
                detectedContentType = sourceResult.contentType || blob.type || getMimeTypeFromFileName(selectedStoreFile);
                fileToUpload = new File([blob], finalFileName.split('/').pop(), { 
                  type: detectedContentType
                });
              } catch (error) {
                console.error('Error reading source file:', error);
                throw new Error(`Failed to read source file: ${error.message}`);
              }
            } else if (fileDataToSave && fileDataToSave.length > 0) {
              // Media file from table data
              const mediaRecord = fileDataToSave[0];
              const mediaUrl = mediaRecord.previewUrl || mediaRecord.preview;
              
              if (mediaUrl) {
                const response = await fetch(mediaUrl);
                if (!response.ok) throw new Error('Failed to fetch media file');
                
                const blob = await response.blob();
                detectedContentType = blob.type || getMimeTypeFromFileName(mediaUrl);
                fileToUpload = new File([blob], finalFileName.split('/').pop(), { 
                  type: detectedContentType
                });
              } else {
                throw new Error('No media file available for upload');
              }
            } else {
              throw new Error('No media file selected for upload');
            }

            // Ensure we have a content type
            if (!detectedContentType) {
              detectedContentType = getMimeTypeFromFileName(finalFileName);
            }

            // Upload media file
            await doUpload(fullFilePath, fileToUpload, detectedContentType, destBucket);

          } else {
            // Handle data file (CSV/Excel)
            let fileContent;
            let contentType;
            
            if (saveFormat === 'csv') {
              fileContent = Papa.unparse(fileDataToSave || []);
              contentType = 'text/csv';
            } else {
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet('Data');
              
              if (fileDataToSave && fileDataToSave.length > 0) {
                const headers = Object.keys(fileDataToSave[0]);
                worksheet.addRow(headers);
                
                fileDataToSave.forEach(row => {
                  const rowData = headers.map(header => row[header] || '');
                  worksheet.addRow(rowData);
                });
              }
              
              fileContent = await workbook.xlsx.writeBuffer();
              contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            }

            // Convert to File object for upload
            const fileBlob = new Blob([fileContent], { type: contentType });
            const dataFile = new File([fileBlob], finalFileName.split('/').pop(), { 
              type: contentType 
            });

            await doUpload(fullFilePath, dataFile, contentType, destBucket);
          }

          imperativeAlert(`File successfully saved to bucket: ${destBucket}\nFile: ${fullFilePath}`);

          // Refresh file list if we're in store connection
          if (activeConnection === 'store' && destBucket === selectedBucket) {
            fetchStoreFiles(destFolder || '', destBucket);
          }
          
          // Clear data
          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
          
        } catch (error) {
          console.error('Bucket save error:', error);
          
          // Provide more user-friendly error messages
          let userMessage = error.message;
          if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
            userMessage = 'Server error occurred while uploading. Please try again or contact support.';
          } else if (error.message.includes('403')) {
            userMessage = 'Permission denied. You may not have access to upload to this bucket.';
          } else if (error.message.includes('413')) {
            userMessage = 'File is too large to upload.';
          }
          
          throw new Error(`Failed to save to bucket: ${userMessage}`);
        }
      },

      onClose: () => {
        if (ctx.setFileDataToSave) {
          ctx.setFileDataToSave(null);
        }
      },

      secondaryButton: { 
        label: 'Cancel', 
        onClick: () => {
          if (ctx.setFileDataToSave) {
            ctx.setFileDataToSave(null);
          }
          onClose();
        }
      },
    };
  }

  /* -----------------------------------------------------------------
     Fallback – unknown mode (should never happen)
     ----------------------------------------------------------------- */
  return {
    title: 'Unsupported Save Mode',
    fields: [],
    async onSubmit() {
      imperativeAlert('This save mode is not supported.');
      onClose();
    },
    secondaryButton: { label: 'Close', onClick: onClose },
  };
}